
import { useState } from "react";
import { Product, StockUpdate } from "@/lib/types";
import { generateId } from "@/lib/storage/core";
import { addStockUpdate } from "@/lib/storage/stock/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";

interface StockUpdateFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product;
  onSuccess: () => void;
}

const StockUpdateForm = ({ 
  open, 
  onOpenChange, 
  product,
  onSuccess
}: StockUpdateFormProps) => {
  const [quantity, setQuantity] = useState<number>(1);
  const [date, setDate] = useState<Date>(new Date());
  const [useFullPackage, setUseFullPackage] = useState<boolean>(false);
  const [numberOfPackages, setNumberOfPackages] = useState<number>(1);
  const { toast } = useToast();
  
  const handleSubmit = () => {
    let finalQuantity = useFullPackage && product.packageSize 
      ? numberOfPackages * product.packageSize 
      : quantity;
      
    if (finalQuantity <= 0) {
      toast({
        title: "Invalid quantity",
        description: "Please enter a positive quantity",
        variant: "destructive"
      });
      return;
    }
    
    const update: StockUpdate = {
      id: generateId(),
      productId: product.id,
      quantity: finalQuantity,
      date,
    };
    
    addStockUpdate(update);
    toast({
      title: "Stock Updated",
      description: `Added ${finalQuantity} units to ${product.name}`,
    });
    onSuccess();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] glass-card animate-fade-in">
        <DialogHeader>
          <DialogTitle>
            Add Stock to {product.name}
          </DialogTitle>
          <DialogDescription>
            Update your inventory with new supplies.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Current stock: <span className="font-medium text-gray-900 dark:text-gray-100">{product.currentStock} units</span>
            </p>
          </div>
          
          {product.packageSize && product.packageSize > 0 && (
            <div className="flex items-start space-x-2 pt-2">
              <Checkbox 
                id="use-full-package" 
                checked={useFullPackage}
                onCheckedChange={(checked) => {
                  setUseFullPackage(checked === true);
                }}
              />
              <div className="grid gap-1.5 leading-none">
                <Label 
                  htmlFor="use-full-package" 
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  Add full package(s)
                </Label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Each package contains {product.packageSize} units
                </p>
              </div>
            </div>
          )}
          
          {useFullPackage && product.packageSize && product.packageSize > 0 ? (
            <div className="space-y-2">
              <Label htmlFor="packages">Number of Packages</Label>
              <Input
                id="packages"
                type="number"
                min="1"
                value={numberOfPackages}
                onChange={(e) => setNumberOfPackages(Number(e.target.value))}
                className="form-input"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Total units: {numberOfPackages * product.packageSize}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity to Add</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className="form-input"
              />
            </div>
          )}
          
          <div className="space-y-2">
            <Label>Date Received</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white dark:bg-gray-800" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(date) => date && setDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="pt-2">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              New stock after update: <span className="font-medium text-gray-900 dark:text-gray-100">
                {product.currentStock + (useFullPackage && product.packageSize ? numberOfPackages * product.packageSize : quantity)} units
              </span>
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={useFullPackage ? numberOfPackages < 1 : quantity < 1}
            className="btn-primary"
          >
            Add to Inventory
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StockUpdateForm;
